import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Globe } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Admin tab: visitors by country with share percentages */
export const CountriesPanel = ({ adminKey }) => {
  const [countries, setCountries] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/analytics/summary`)
      .then(({ data }) => alive && setCountries(data.countries || []))
      .catch(() => alive && setErr("Failed to load country data."));
    return () => { alive = false; };
  }, [adminKey]);

  if (err) return <div className="text-red-400 text-sm">{err}</div>;
  if (countries === null)
    return (
      <div className="flex items-center gap-3 text-zinc-400">
        <Loader2 className="animate-spin" size={16} /> Loading…
      </div>
    );

  const totalVisitors = countries.reduce((s, c) => s + (c.visitors || 0), 0) || 1;
  const totalViews = countries.reduce((s, c) => s + (c.views || 0), 0) || 1;

  return (
    <div className="space-y-6" data-testid="countries-panel">
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
        <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-400 mb-6 flex items-center gap-2">
          <Globe size={12} /> Where your visitors come from
        </div>

        {countries.length === 0 && (
          <div className="text-zinc-500 text-sm">
            No geo data yet — it's collected from every new visit going forward.
          </div>
        )}

        <div className="space-y-4">
          {countries.map((c) => {
            const pct = Math.round(((c.visitors || 0) / totalVisitors) * 1000) / 10;
            const viewPct = Math.round(((c.views || 0) / totalViews) * 1000) / 10;
            return (
              <div key={c.country} data-testid={`country-row-${c.country}`}>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="text-zinc-100">{c.country}</span>
                  <span className="text-right">
                    <span className="text-[#E5C56F] font-semibold">{pct}%</span>
                    <span className="text-zinc-500 text-xs ml-3">
                      {c.visitors} visitor{c.visitors === 1 ? "" : "s"} · {c.views} views ({viewPct}%)
                    </span>
                  </span>
                </div>
                <div className="h-2 rounded-full bg-white/[0.05] overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.max(pct, 2)}%`,
                      background: "linear-gradient(90deg, #7e22ce, #E5C56F)",
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {countries.length > 0 && (
          <div className="mt-6 pt-4 border-t border-white/[0.06] text-xs text-zinc-500">
            {totalVisitors} located visitor{totalVisitors === 1 ? "" : "s"} total · percentages = share of visitors per country
          </div>
        )}
      </div>
    </div>
  );
};

export default CountriesPanel;
